/**
 * Colony-sim data model (see SIM_DESIGN.md). The whole game is one serializable
 * `World`. The simulation is a pure function `(world, action|dt) → world`, so it
 * stays testable and deterministic (randomness flows through `rngSeed`).
 */

export type SkillKey = 'growing' | 'dealing' | 'combat';
export const SKILL_KEYS: SkillKey[] = ['growing', 'dealing', 'combat'];

export type NeedKey = 'rest' | 'food' | 'joy';
export const NEED_KEYS: NeedKey[] = ['rest', 'food', 'joy'];

/** Single product pool (grow → deal). Lab/Pack are optional boosters, not stages. */
export type ResourceKey = 'product' | 'cash' | 'meals';

export type RoomType =
  | 'grow'
  | 'lab'
  | 'pack'
  | 'deal'
  | 'bunk'
  | 'diner'
  | 'rec'
  | 'security'
  | 'stash';

/** What a colonist is doing right now. */
export type Activity =
  | 'idle'
  | 'goto' // walking / riding the elevator to a target
  | 'work'
  | 'sleep'
  | 'eat'
  | 'relax'
  | 'flee'
  | 'recover' // injured, in a bunk
  | 'arrested';

export interface Skill {
  level: number; // 0..20
  xp: number; // toward next level
}
export type Skills = Record<SkillKey, Skill>;
export type Needs = Record<NeedKey, number>; // 0..100 (100 = fully satisfied)

export interface Thought {
  id: string;
  label: string;
  mood: number; // +/- mood offset
  expires: number; // sim-seconds
}

export interface Colonist {
  id: number;
  name: string;
  skin: string;
  accent: string; // clothing accent
  traits: string[];
  skills: Skills;
  needs: Needs;
  mood: number; // cached 0..100

  // assignment
  assignedRoomId: number | null;
  homeBunkId: number | null;

  // live state
  activity: Activity;
  floor: number; // current floor index (0 = ground)
  x: number; // 0..1 along the floor (0 = elevator shaft)
  targetRoomId: number | null;
  targetFloor: number;
  targetX: number;
  ride: number; // 0..1 elevator ride progress (while changing floors)

  downUntil: number; // sim-seconds; injury recovery
  breakUntil: number; // mood-break end
  arrestedReturn: number; // sim-seconds a jailed colonist returns (or -1 = gone)
  recentThoughts: Thought[];
}

export interface Room {
  id: number;
  type: RoomType;
  floor: number;
  slot: number; // index along the floor
  tier: number;
  damagedUntil: number; // sim-seconds disabled (0 = operational)
  progress: number; // 0..1 production cycle (visual)
}

export interface RaidState {
  resolveAt: number; // sim-seconds the raid hits
  strength: number;
  bribeCost: number;
  bracedRoomId: number | null; // player-picked room to protect during the telegraph
}

export interface ActiveEvent {
  id: string;
  label: string;
  endsAt: number;
  /** effect modifiers while active */
  dealMult?: number;
  heatPerSec?: number;
}

export interface RecruitOffer {
  colonist: Colonist;
  cost: number;
  expiresAt: number;
}

export interface LogEntry {
  t: number;
  day: number;
  kind: 'info' | 'good' | 'bad' | 'raid';
  text: string;
}

/** A transient cross-building notice (e.g. a raid on a building you're not
 *  currently managing). Self-expires; the log (above) keeps the permanent record. */
export interface Toast {
  id: number;
  kind: 'raid' | 'good' | 'bad';
  text: string;
  buildingId: number;
  expiresAt: number; // sim-seconds
}

import type { CityPlot, BSize, BuildingArt } from './config/plots';
export type { CityPlot, BSize, BuildingArt };

/** One building (a tower). Has its own floors, rooms, crew, larder, heat, raids. */
export interface Building {
  id: number;
  name: string;
  district: string;
  mapX: number;
  mapY: number;
  size: BSize;
  art: BuildingArt;

  // structure — floor 0 is the street; negatives are basements, positives upper
  floorMin: number;
  floorMax: number;
  rooms: Room[];
  nextRoomId: number;

  // people who work/live here (capped by maxCrew)
  colonists: Colonist[];
  nextColonistId: number;
  maxCrew: number;

  // local resources (the empire's cash is shared at City level)
  res: { product: number; meals: number };

  // risk
  heat: number; // 0..100
  layingLow: boolean;
  security: Record<string, number>;
  raid: RaidState | null;
  raidTimer: number;
}

/** The empire: many buildings across the city + a shared bankroll & clock. */
export interface City {
  version: number;

  // shared clock + bankroll
  t: number;
  speed: number; // 0 = paused, else 1|2|3
  cash: number;
  rep: number; // permanent reputation → sale-value multiplier
  runEarned: number; // lifetime cash earned (goals)

  // the empire
  buildings: Building[];
  plots: CityPlot[]; // unowned plots still on the map
  activeId: number; // the building currently being managed (0 = none yet)
  nextBuildingId: number;

  // storyteller (city-wide)
  eventTimer: number;
  activeEvents: ActiveEvent[];
  recruit: RecruitOffer | null;
  completedGoals: string[];

  // meta
  rngSeed: number;
  lastSeen: number;
  log: LogEntry[];
  toasts: Toast[];
  nextToastId: number;

  // run stats
  raidsSurvived: number;
  raidsFaced: number;
  arrested: number;
}

// --- Actions: every transition goes through the reducer ---------------------
// Building-scoped actions act on the active building (the one you're managing).
export type SimAction =
  | { type: 'tick'; dt: number } // dt = sim-seconds (already scaled by speed)
  | { type: 'setSpeed'; speed: number }
  | { type: 'switchBuilding'; buildingId: number }
  | { type: 'buyPlot'; plotId: number }
  | { type: 'assign'; colonistId: number; roomId: number | null }
  | { type: 'buildRoom'; floor: number; slot: number; roomType: RoomType }
  | { type: 'addFloor' }
  | { type: 'digBasement' }
  | { type: 'buySecurity'; id: string }
  | { type: 'toggleLayLow' }
  | { type: 'bribeRaid' }
  | { type: 'acceptRecruit' }
  | { type: 'dismissRecruit' }
  | { type: 'repairRoom'; roomId: number }
  | { type: 'braceRoom'; roomId: number }
  | { type: 'dismissToast'; id: number };
